import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import './Tour.css';

const TOUR_STORAGE_KEY = 'koolshade-tour-completed';
const TOUR_STARTED_KEY = 'koolshade-tour-started';

// Tour steps configuration
const getTourSteps = (currentPath) => {
  const baseSteps = [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Welcome to KoolShade AI Knowledge Assistant! 👋
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            This quick tour will help you get the most out of the application. You can exit anytime by clicking the X button.
          </p>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
            Let's explore the three main modes: <strong>Ask</strong>, <strong>Write</strong>, and <strong>Documents</strong>.
          </p>
        </div>
      ),
      placement: 'center',
    },
  ];

  // Steps for Ask page
  const askSteps = [
    {
      target: '.sidebar-nav',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Navigation
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            Use the sidebar to switch between three main modes:
          </p>
          <ul style={{ margin: '8px 0', paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Ask</strong> - Query your knowledge base</li>
            <li><strong>Write</strong> - AI writing assistant</li>
            <li><strong>Documents</strong> - Manage your documents</li>
          </ul>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '.technicality-slider-container',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Technicality Level
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            Adjust the technicality slider to control how detailed and technical the AI's responses are.
          </p>
          <ul style={{ margin: '8px 0', paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Simple (1-2)</strong> - Easy to understand, less technical</li>
            <li><strong>Moderate (3)</strong> - Balanced explanation</li>
            <li><strong>Expert (4-5)</strong> - Highly technical and detailed</li>
          </ul>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '.chat-input-form textarea',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Ask Questions
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            Type your question here about technical drawings, procedures, or documentation. The AI will search through your uploaded documents to provide accurate answers.
          </p>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
            Press <strong>Enter</strong> to send, or <strong>Shift+Enter</strong> for a new line.
          </p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '.chat-container, .write-messages-container',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Web Search Feature 🌐
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            After receiving an answer, look for the <strong>Web Search</strong> button below assistant messages. Click it to enhance the response with real-time information from the internet.
          </p>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
            This combines your knowledge base with current web information for the most comprehensive answers.
          </p>
        </div>
      ),
      placement: 'center',
    },
    {
      target: '.sources-panel',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Sources & Evidence
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            The right panel shows all documents and sources referenced in the AI's answer. Click on any source to view the original document.
          </p>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
            This helps you verify information and dive deeper into specific documents.
          </p>
        </div>
      ),
      placement: 'left',
    },
  ];

  // Steps for Write page
  const writeSteps = [
    {
      target: '.sidebar-nav',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Write Mode
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            The <strong>Write</strong> mode is your AI writing assistant. It helps you draft emails, responses, and other documents.
          </p>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
            You can attach files for context, and the AI will help you write based on your knowledge base and attached files.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '.write-input-textarea',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Writing Assistant
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            Describe what you want to write - like "Draft a professional email to a client" or "Write a response to a customer inquiry".
          </p>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
            The AI will generate drafts based on your knowledge base and any attached files.
          </p>
        </div>
      ),
      placement: 'top',
    },
  ];

  // Steps for Documents page
  const documentsSteps = [
    {
      target: '.sidebar-nav',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Documents Mode
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            The <strong>Documents</strong> page is where you manage your knowledge base. Upload PDFs, Word documents, and other files.
          </p>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
            Once uploaded, these documents become searchable and can be referenced in Ask and Write modes.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="upload-area"]',
      content: (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
            Upload Documents
          </h3>
          <p style={{ marginBottom: '8px', lineHeight: '1.6' }}>
            Drag and drop files here, or click to browse. Supported formats include PDF, Word, PowerPoint, and more.
          </p>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '14px', color: '#666' }}>
            Documents are processed automatically and become searchable within minutes.
          </p>
        </div>
      ),
      placement: 'top',
    },
  ];

  // Combine steps based on current path
  if (currentPath === '/ask' || currentPath === '/') {
    return [...baseSteps, ...askSteps];
  } else if (currentPath === '/write') {
    return [...baseSteps, ...writeSteps];
  } else if (currentPath === '/documents') {
    return [...baseSteps, ...documentsSteps];
  }
  
  return baseSteps;
};

function Tour() {
  const location = useLocation();
  const { user, isLoaded } = useUser();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [hasChecked, setHasChecked] = useState(false);
  const [overlayStyle, setOverlayStyle] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState(null);
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);
  const highlightedElementRef = useRef(null);
  const previousHighlightRef = useRef(null);

  // Check if tour should run on mount (only once, and only when user is signed in)
  useEffect(() => {
    // Wait for auth to load and user to be available
    if (!isLoaded || !user || hasChecked) return;
    
    const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
    const tourStarted = localStorage.getItem(TOUR_STARTED_KEY) === 'true';
    
    // Only auto-start if not completed and not already started
    if (!tourCompleted && !tourStarted) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        setSteps(getTourSteps(location.pathname));
        setRun(true);
        localStorage.setItem(TOUR_STARTED_KEY, 'true');
        setHasChecked(true);
      }, 1000);
    } else {
      setHasChecked(true);
    }
  }, [isLoaded, user, location.pathname, hasChecked]);

  // Update steps when path changes during tour
  useEffect(() => {
    if (run) {
      setSteps(getTourSteps(location.pathname));
    }
  }, [location.pathname, run]);

  // Cleanup function for highlighted elements
  const cleanupHighlight = () => {
    if (highlightedElementRef.current) {
      const el = highlightedElementRef.current;
      el.style.outline = '';
      el.style.outlineOffset = '';
      el.style.zIndex = '';
      el.style.position = '';
      el.classList.remove('tour-highlighted');
      highlightedElementRef.current = null;
    }
    if (previousHighlightRef.current) {
      const el = previousHighlightRef.current;
      el.style.outline = '';
      el.style.outlineOffset = '';
      el.style.zIndex = '';
      el.style.position = '';
      el.classList.remove('tour-highlighted');
      previousHighlightRef.current = null;
    }
  };

  // Position tooltip and overlay
  useEffect(() => {
    if (!run || steps.length === 0 || stepIndex >= steps.length) {
      // Clean up when tour stops
      cleanupHighlight();
      setOverlayStyle(null);
      setTooltipStyle(null);
      return;
    }

    const step = steps[stepIndex];
    if (!step) return;

    // Clean up previous highlight
    cleanupHighlight();
    previousHighlightRef.current = highlightedElementRef.current;

    const updatePosition = () => {
      if (step.placement === 'center') {
        // Center placement - overlay covers everything
        setOverlayStyle({
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
        });
        setTooltipStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
          maxWidth: '500px',
        });
      } else {
        // Find target element
        const targetElement = document.querySelector(step.target);
        if (!targetElement) {
          // If target not found, use center placement
          setOverlayStyle({
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
          });
          setTooltipStyle({
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            maxWidth: '500px',
          });
          return;
        }

        const rect = targetElement.getBoundingClientRect();
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        // Store reference for cleanup
        highlightedElementRef.current = targetElement;

        // Create overlay - simple full overlay (highlighting will be done via outline on target)
        setOverlayStyle({
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
        });
        
        // Add highlight to target element with animation
        targetElement.style.outline = '3px solid #0066cc';
        targetElement.style.outlineOffset = '4px';
        targetElement.style.zIndex = '9999';
        targetElement.style.position = 'relative';
        targetElement.style.transition = 'outline 0.3s ease, outline-offset 0.3s ease';
        targetElement.classList.add('tour-highlighted');

        // Position tooltip with smart placement to avoid conflicts
        let tooltipTop, tooltipLeft;
        const tooltipWidth = 500;
        const tooltipHeight = 280; // More accurate estimate
        const spacing = 20;
        const viewportPadding = 20;

        // Calculate initial position based on placement
        switch (step.placement) {
          case 'top':
            tooltipTop = rect.top + scrollY - tooltipHeight - spacing;
            tooltipLeft = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'bottom':
            tooltipTop = rect.bottom + scrollY + spacing;
            tooltipLeft = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'left':
            tooltipTop = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
            tooltipLeft = rect.left + scrollX - tooltipWidth - spacing;
            break;
          case 'right':
            tooltipTop = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
            tooltipLeft = rect.right + scrollX + spacing;
            break;
          default:
            tooltipTop = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
            tooltipLeft = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
        }

        // Smart viewport adjustment - keep tooltip in viewport and avoid overlaps
        const maxLeft = window.innerWidth - tooltipWidth - viewportPadding;
        const maxTop = window.innerHeight - tooltipHeight - viewportPadding;
        const minLeft = viewportPadding;
        const minTop = viewportPadding;

        // Adjust horizontally
        if (tooltipLeft < minLeft) {
          tooltipLeft = minLeft;
        } else if (tooltipLeft > maxLeft) {
          tooltipLeft = maxLeft;
        }

        // Adjust vertically
        if (tooltipTop < minTop) {
          // If top placement doesn't fit, try bottom
          if (step.placement === 'top') {
            tooltipTop = rect.bottom + scrollY + spacing;
          } else {
            tooltipTop = minTop;
          }
        } else if (tooltipTop > maxTop + scrollY) {
          // If bottom placement doesn't fit, try top
          if (step.placement === 'bottom') {
            tooltipTop = rect.top + scrollY - tooltipHeight - spacing;
          } else {
            tooltipTop = maxTop + scrollY;
          }
        }

        // Final viewport check
        tooltipLeft = Math.max(minLeft, Math.min(tooltipLeft, maxLeft));
        tooltipTop = Math.max(minTop, Math.min(tooltipTop, maxTop + scrollY));

        setTooltipStyle({
          position: 'fixed',
          top: `${tooltipTop}px`,
          left: `${tooltipLeft}px`,
          zIndex: 9999,
          maxWidth: `${tooltipWidth}px`,
        });
      }
    };

    // Small delay for smooth transition
    const timeoutId = setTimeout(() => {
      updatePosition();
    }, 50);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      cleanupHighlight();
    };
  }, [run, stepIndex, steps]);

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleSkip = () => {
    handleFinish();
  };

  const handleFinish = () => {
    cleanupHighlight();
    setRun(false);
    setOverlayStyle(null);
    setTooltipStyle(null);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    localStorage.removeItem(TOUR_STARTED_KEY);
  };

  // Expose restart function via window for the help button
  useEffect(() => {
    window.startTour = () => {
      localStorage.removeItem(TOUR_STORAGE_KEY);
      localStorage.removeItem(TOUR_STARTED_KEY);
      setSteps(getTourSteps(location.pathname));
      setStepIndex(0);
      setRun(true);
    };
    return () => {
      delete window.startTour;
    };
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (highlightedElementRef.current) {
        const el = highlightedElementRef.current;
        el.style.outline = '';
        el.style.outlineOffset = '';
        el.style.zIndex = '';
        el.style.position = '';
        el.classList.remove('tour-highlighted');
      }
      if (previousHighlightRef.current) {
        const el = previousHighlightRef.current;
        el.style.outline = '';
        el.style.outlineOffset = '';
        el.style.zIndex = '';
        el.style.position = '';
        el.classList.remove('tour-highlighted');
      }
      setOverlayStyle(null);
      setTooltipStyle(null);
    };
  }, []);

  if (!run || steps.length === 0 || stepIndex >= steps.length) {
    return null;
  }

  const currentStep = steps[stepIndex];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;

  return (
    <>
      <div 
        ref={overlayRef}
        className="tour-overlay" 
        style={overlayStyle}
        onClick={handleSkip}
      />
      <div 
        ref={tooltipRef}
        className="tour-tooltip" 
        style={tooltipStyle}
      >
        <button 
          className="tour-close-btn"
          onClick={handleSkip}
          aria-label="Close tour"
        >
          <X style={{ width: '18px', height: '18px' }} />
        </button>
        <div className="tour-content">
          {currentStep.content}
        </div>
        <div className="tour-footer">
          <div className="tour-progress">
            {stepIndex + 1} of {steps.length}
          </div>
          <div className="tour-buttons">
            {!isFirstStep && (
              <button 
                className="tour-btn tour-btn-secondary"
                onClick={handleBack}
              >
                <ChevronLeft style={{ width: '16px', height: '16px' }} />
                Back
              </button>
            )}
            <button 
              className="tour-btn tour-btn-skip"
              onClick={handleSkip}
            >
              Skip Tour
            </button>
            <button 
              className="tour-btn tour-btn-primary"
              onClick={handleNext}
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <ChevronRight style={{ width: '16px', height: '16px' }} />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Helper function to restart tour (can be called from anywhere)
export const restartTour = () => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
  localStorage.removeItem(TOUR_STARTED_KEY);
  window.location.reload();
};

export default Tour;
